import React, { useState, useEffect, useMemo, useCallback } from "react";

const SearchAndFilterComponent = ({
  allProjects,
  onItemSelection,
  loading,
  setLoading,
  viewMaterialDetails,
  searchQuery,
  setSearchQuery,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  handleExcelExport,
  selectedTab,
  projectIdFromVehicle,
  currentProject,
  inwardItems,
  outwardItems,
  filteredInwardItems,
  filteredOutwardItems,
  onCategoryFilterChange,
}) => {
  const [hovered, setHovered] = useState(false);
  // Local state for input value to prevent typing lag
  const [localSearchInput, setLocalSearchInput] = useState(searchQuery);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Initialize local state when searchQuery prop changes from outside
  useEffect(() => {
    setLocalSearchInput(searchQuery);
  }, [searchQuery]);


  useEffect(() => {
    setSelectedCategory('all');
  }, [currentProject])

  // Effect to handle date range validation
  useEffect(() => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start date cannot be after end date");
      setEndDate(startDate);
    }
  }, [startDate, endDate, setEndDate]);

  // Memoize this calculation to improve performance
  const currentValue = useMemo(() => {
    if (projectIdFromVehicle === "All Sites") return "o";
    if (Array.isArray(currentProject)) return "o";
    
    const index = allProjects.findIndex((p) => p.projectID === currentProject.projectID);
    return index >= 0 ? index.toString() : "0";
  }, [projectIdFromVehicle, currentProject, allProjects]);

  // Optimized project change handler with useCallback
  const handleProjectChange = useCallback(async (e) => {
    const newValue = e.target.value;
    const selectedProject = newValue === "o" ? null : allProjects[newValue];

    setLoading(true);
    const success = await onItemSelection(selectedProject);
    if (!success) {
      e.target.value = currentValue;
    }
  }, [allProjects, onItemSelection, setLoading, currentValue]);

  // Extract unique categories from the current data
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    
    if (selectedTab === "Inward") {
      // Process inward items for categories
      const itemsToProcess = filteredInwardItems.length > 0 ? filteredInwardItems : inwardItems;
      console.log("Processing inward items for categories:", itemsToProcess.length);
      
      itemsToProcess.forEach(item => {
        if (item.invoices) {
          Object.values(item.invoices).forEach(invoice => {
            if (invoice.invoiceItems) {
              Object.values(invoice.invoiceItems).forEach(invoiceItem => {
                if (invoiceItem.category) {
                  uniqueCategories.add(invoiceItem.category);
                }
              });
            }
          });
        }
      });
    } else if (selectedTab === "Outward") {
      // Process outward items for categories
      const itemsToProcess = filteredOutwardItems.length > 0 ? filteredOutwardItems : outwardItems;
      console.log("Processing outward items for categories:", itemsToProcess.length);
      
      itemsToProcess.forEach(item => {
        if (item.outwardItems) {
          Object.values(item.outwardItems).forEach(outwardItem => {
            if (outwardItem.category) {
              uniqueCategories.add(outwardItem.category);
            }
          });
        }
      });
    }
    
    const result = Array.from(uniqueCategories).sort();
    console.log("Found unique categories:", result.length, result);
    return result;
  }, [selectedTab, inwardItems, outwardItems, filteredInwardItems, filteredOutwardItems]);

  // Handle category change
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    onCategoryFilterChange(category);
  };

  // Optimized search handler with better debounce
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    
    // Update local state immediately for responsive typing
    setLocalSearchInput(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set longer timeout for actual search update to reduce processing frequency
    const timeout = setTimeout(() => {
      setSearchQuery(value);
    }, 500); // Increased debounce time
    
    setSearchTimeout(timeout);
  }, [searchTimeout, setSearchQuery]);
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Helper function to format date consistently (for date inputs)
  const formatDateString = (dateString) => {
    if (!dateString) return "";
    
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse the date and format it
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.log("Error formatting date:", e);
      return dateString;
    }
  };
  
  // Handle date input changes with validation
  const handleStartDateChange = (e) => {
    const rawValue = e.target.value;
    console.log("Start date input changed to:", rawValue);
    const formattedValue = formatDateString(rawValue);
    setStartDate(formattedValue);
  };
  
  const handleEndDateChange = (e) => {
    const rawValue = e.target.value;
    console.log("End date input changed to:", rawValue);
    const formattedValue = formatDateString(rawValue);
    setEndDate(formattedValue);
  };

  const styles = {
    container: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#fff",
      position: "relative",
      left: 0,
      right: 0,
    },
    dateLabelsContainer: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%",
      padding: "0 20px",
      gap: "10px",
    },
    dateLabel: {
      fontSize: "14px",
      color: "#555",
      textAlign: "left",
      marginBottom: "5px",
    },
    searchContainer: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      padding: "10px 20px",
      gap: "10px",
      backgroundColor: "#fff",
    },
    dropdown: {
      width: "200px",
      padding: "10px",
      borderRadius: "5px",
      border: "1px solid #ddd",
      fontSize: "16px",
      height: "42px",
    },
    searchInput: {
      flex: "1",
      padding: "10px",
      borderRadius: "5px",
      border: "1px solid #ddd",
      fontSize: "16px",
      height: "42px",
    },
    categoryDropdown: {
      width: "200px",
      padding: "10px",
      borderRadius: "5px",
      border: "1px solid #ddd",
      fontSize: "16px",
      height: "42px",
      backgroundColor: selectedCategory !== "all" ? "#e6f7ff" : "#fff",
    },
    dateInput: {
      width: "200px",
      padding: "10px",
      fontSize: "16px",
      borderRadius: "5px",
      border: "1px solid #ddd",
      height: "42px",
    },
    button: {
      padding: "10px 20px",
      backgroundColor: "#0066cc",
      color: "#fff",
      border: "none",
      borderRadius: "5px",
      fontSize: "16px",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
      height: "42px",
      whiteSpace: "nowrap",
    },
    buttonHover: {
      backgroundColor: "#0055bb",
    },
    disabledDropdown: {
      backgroundColor: "#f5f5f5",
      cursor: "not-allowed",
    },
  };

  // Memoize button style to prevent recalculations
  const buttonStyle = useMemo(() => {
    return {
      ...styles.button,
      ...(hovered && styles.buttonHover),
      ...{
        backgroundColor: selectedTab === "Inward" ? "#28a745" : "#dc3545",
      },
    };
  }, [hovered, selectedTab]);

  return (
    <div style={styles.container}>
      {selectedTab === "Inward" && (
        <div style={styles.dateLabelsContainer}>
          <div style={{ flex: "0 0 200px" }}></div>
          {!viewMaterialDetails && <div style={{ flex: 1 }}></div>}
          {!viewMaterialDetails && selectedTab !== "All Materials" && (
            <div style={{ width: "200px" }}>
              <div style={styles.dateLabel}>Category:</div>
            </div>
          )}
          <div style={{ width: "200px" }}>
            <div style={styles.dateLabel}>Start Date:</div>
          </div>
          <div style={{ width: "200px" }}>
            <div style={styles.dateLabel}>End Date:</div>
          </div>
          <div style={{ width: "120px" }}></div>
        </div>
      )}

      <div style={styles.searchContainer}>
        <select
          style={{
            ...styles.dropdown,
            ...(projectIdFromVehicle && styles.disabledDropdown),
          }}
          onChange={handleProjectChange}
          disabled={loading || projectIdFromVehicle}
          value={currentValue}
        >
          {allProjects.map((item, index) => (
            <option key={index} value={index}>
              {item.projectName}
            </option>
          ))}
          <option key={allProjects.length} value={"o"}>
            All Sites
          </option>
        </select>

        {!viewMaterialDetails && (
          <>
            <input
              type="text"
              value={localSearchInput}
              onChange={handleSearchChange}
              style={styles.searchInput}
              placeholder="Search for materials..."
            />
            
            {selectedTab !== "All Materials" && (
              <select
                style={styles.categoryDropdown}
                value={selectedCategory}
                onChange={handleCategoryChange}
                disabled={loading}
              >
                <option value="all">All Categories</option>
                {categories.map((category, index) => (
                  <option key={index} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            )}
          </>
        )}

        {selectedTab !== "All Materials" && (
          <>
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              style={styles.dateInput}
              max={endDate || undefined}
            />

            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              style={styles.dateInput}
              min={startDate || undefined}
            />

            <button
              onClick={handleExcelExport}
              style={buttonStyle}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
            >
              Export to Excel
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(SearchAndFilterComponent);
